import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, fork, takeLatest, put, select, delay } from 'redux-saga/effects';
import { actionRequest, moduleRequest } from 'core/services/product';
import { RootState } from 'types';
import { EntityTypes } from 'constants/entities';
import { IUpdateState, updateState } from 'core/actions/product/state';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';
import { IModuleDAO, ModuleStage, ModuleStatus, ModuleType } from 'types/domain/module';
import { getRequest } from 'core/services/admin';
import { selectActionRegister } from 'core/selectors/product/action/actionRegister';

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  const mode: Mode = yield select(selectSessionMode);

  if (!action.payload.id || mode !== Mode.ADMIN) return;

  switch (action.payload.type) {
    case EntityTypes.MODULE:
      yield loadModule(action.payload.id);

      break;
  }
}

function* getRequestSaga(action) {
  if (action.meta.arg.type === EntityTypes.MODULE && action.meta.arg.id) yield loadModule(action.meta.arg.id);
}

function* moduleRequestFailedSaga(action) {
  yield delay(200);
  const asset = yield select((state: RootState) => state.core.assets[`module_${action.meta.arg.id}`]);

  if (asset && asset.attempts < 6) yield loadModule(action.meta.arg.id);
}

function* actionRequestFailedSaga(action) {
  yield delay(200);
  const actionRegister = yield select((state: RootState) => selectActionRegister(state, action.meta.arg.actionId));

  if (actionRegister && actionRegister.attempts < 6) yield put(yield call(actionRequest, action.meta.arg));
}

export function* loadModule(id) {
  const prodEnts = yield select((state: RootState) => state.entity.present);
  const module: IModuleDAO = prodEnts[EntityTypes.MODULE][id];
  const mode: Mode = yield select(selectSessionMode);

  if (module && [ModuleType.WEBSITE, ModuleType.YOUTUBE, ModuleType.SCORM].includes(module.type)) return false;

  //added a learning check into this because getting stuck on first module when loading after having been in admin
  if (id && (!module || module.__stale || module.__stage !== ModuleStage.DEEP || mode === Mode.LEARNING)) {
    yield put(
      yield call(moduleRequest, {
        id: id,
        //url: null,
        url: mode === Mode.LEARNING && module?.status === ModuleStatus.ACTIVE && module?.url ? `/${module?.url}` : null,
      }),
    );
    return true;
  }
  return false;
}

function* watchModuleRequestFailed() {
  yield takeLatest(moduleRequest.rejected, moduleRequestFailedSaga);
}

function* watchActionRequestFailed() {
  yield takeLatest(actionRequest.rejected, actionRequestFailedSaga);
}

function* watchGetRequest() {
  yield takeLatest(getRequest.fulfilled, getRequestSaga);
}

function* watchUpdateState() {
  yield takeLatest(updateState.type, updateStateSaga);
}

export function* loadSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchGetRequest),
    fork(watchModuleRequestFailed),
    fork(watchActionRequestFailed),
  ]);
}
